/**
 * Converting a food's stored nutrient values between reference amounts: what 100 grams
 * holds, and what one serving holds.
 *
 * Plain arithmetic that knows nothing about any page. It used to live beside the Edit Food
 * page's nutrition label, but the food list and the ingredient editor need it too, and
 * importing it from another page's folder made those pages look like dependencies of it.
 *
 * Both functions divide every key they are given, so a nutrient present with an explicit
 * `null` becomes `0` and one present with `undefined` becomes `NaN`. That differs from
 * `nutrientsForWeight` in `@cf-shared/data-model/ingredient`, which turns both into zero —
 * do not merge the two without reading `design-docs/food-data-quality.md`, which depends on
 * the difference.
 */

import { Nutrients, FoodMeasure } from '@cf-shared/data-model/food';

/** Converts a recipe's total nutrient values to what 100 grams of it holds. */
export function scaleNutrientsToPer100g(nutrients: Nutrients, totalWeight_g: number): Nutrients {
  const scaled: Nutrients = { ...nutrients };
  for (let key in scaled) {
    scaled[key] = (scaled[key] / totalWeight_g) * 100;
  }
  return scaled;
}

/**
 * Converts stored per-100 g nutrient values to what one serving of the food holds. The
 * serving is the whole measure, even one counting several units ("2 bars, 100 g" is 100 g),
 * because that is what the Edit Food page names above the values (#6024).
 */
export function scaleNutrientsToServing(
  nutrients: Nutrients,
  servingMeasure: FoodMeasure,
): Nutrients {
  const scaled: Nutrients = { ...nutrients };
  const servingWeight_g = servingMeasure.weight_g;
  for (let key in scaled) {
    scaled[key] = (scaled[key] / 100) * servingWeight_g;
  }
  return scaled;
}
